import { Component, inject, signal, computed, ViewChild, TemplateRef, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import Dropzone, {DropzoneFile} from "dropzone";
import { ImportManagerService } from "../../services/import-manager.service";
import { PredefinedSegment } from "../../services/cfa.service";
import { LottieComponent } from "ngx-lottie";
import { FeatureBaseService } from "../../services/featurebase.service";
import { ToastNotificationService } from "../../services/toast-notification.service";
import { CfaService } from "../../services/cfa.service";
import { map, shareReplay, startWith } from "rxjs";
import { RouterModule } from "@angular/router";

@Component({
    selector: "app-import-subscribers-modal",
    imports: [CommonModule, FormsModule, LottieComponent, RouterModule],
    templateUrl: "./import-subscribers-modal.component.html",
    styleUrl: "./import-subscribers-modal.component.scss",
    standalone: true
})
export class ImportSubscribersModalComponent implements OnInit {
    private readonly _modalService = inject(NgbModal);
    private readonly _importManagerService = inject(ImportManagerService);
    private readonly _featureBaseService = inject(FeatureBaseService);
    private readonly _toast = inject(ToastNotificationService);
    private readonly _cfaService = inject(CfaService)

    @ViewChild("importModal") importModal!: TemplateRef<any>;
    private importModalRef: NgbModalRef | null = null;

    modalState = signal<"open" | "closed">("closed");
    stage = signal<"segment-select" | "upload" | "importing" | "success" | "error">("segment-select");
    progress = signal(30);
    useSegments = signal(false);
    segmentSelect = signal<"actualReaders" | "freeSubscribers" | "arcReaders">("freeSubscribers");
    forceWelcomeAutomation = signal(false);
    importMethod = signal<"csv" | "paste">("csv");
    importLabel = computed(() => {
        if (this.useSegments()) {
            const segmentSelect = this.segmentSelect();
            if (segmentSelect === "actualReaders") {
                return "actual readers";
            } else if (segmentSelect === "freeSubscribers") {
                return "free subscribers";
            } else {
                return "ARC readers"
            }
        } else {
            return "subscribers"
        }
    });
    pastedText = signal<string>("");
    pastedSubscribers = computed(() => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const subscribers = new Set<string>();
        for (const part of this.pastedText().split(",")) {
            if (emailRegex.test(part.trim())) {
                subscribers.add(part.trim());
            }
        }
        return Array.from(subscribers);
    });
    csvFile = signal<DropzoneFile | null>(null);
    analyzingCSV = signal(false);
    csvSubscriberCount = signal(0);
    csvWarning = signal(false);
    importResultCount = signal(0);

    automationStatus$ = this._cfaService.getWelcomeAutomation().pipe(
        map(automation => {
            if (automation) {
                return {loading: false as const, automation: automation};
            } else {
                return {loading: false as const, automation: null};
            }
        }),
        // shareReplay(1), //fetch it every time it opens so it gets updated after they create it
        startWith({loading: true as const})
    );

    async resetState(closeModal = false) {
        if (closeModal) {
            this.closeImport();
            await new Promise<void>(resolve => this.importModalRef ? this.importModalRef.hidden.subscribe(() => resolve()) : resolve())
        }
        this.stage.set("segment-select");
        this.progress.set(30);
        this.useSegments.set(false);
        this.segmentSelect.set("freeSubscribers");
        this.forceWelcomeAutomation.set(false);
        this.importMethod.set("csv");
        this.pastedText.set("");
        this.csvFile.set(null);
        this.analyzingCSV.set(false);
        this.csvSubscriberCount.set(0);
        this.csvWarning.set(false);
        this.importResultCount.set(0);
    }

    ngOnInit(): void {
        this._importManagerService.importTask$.subscribe((output) => {
            if (output.status === "completed") {
                if (output.data.segments.includes(PredefinedSegment.ActualReaders)) {
                    this.useSegments.set(true);
                    this.segmentSelect.set("actualReaders");
                } else if (output.data.segments.includes(PredefinedSegment.FreeSubscribers)) {
                    this.useSegments.set(true);
                    this.segmentSelect.set("freeSubscribers");
                } else if (output.data.segments.includes(PredefinedSegment.ArcReaders)) {
                    this.useSegments.set(true);
                    this.segmentSelect.set("arcReaders");
                }
                this.importResultCount.set(output.data.createdCount);
                this.goToStage("success");
            } else {
                this.goToStage("error");
            }
        });
    }

    openImport() {
        if (this.modalState() === "open") return;
        this.importModalRef = this._modalService.open(this.importModal, {
            modalDialogClass: "import-modal-dialog",
            //for the case where they closed it and we open it automatically on complete
            //so they see it and don't accidentally close it
            backdrop: this.stage() === "success" || this.stage() === "error" ? "static" : undefined
        });
        this.importModalRef.shown.subscribe(() => this.modalState.set("open"));
        this.importModalRef.hidden.subscribe(() => {
            this.modalState.set("closed");
            //unless it is importing, reset the stage when they close it
            if (this.stage() !== "importing") {
                this.resetState();
            }
        });
    }

    closeImport() {
        if (this.importModalRef) {
            this.importModalRef.close();
        }
    }

    goToStage(stage: "segment-select" | "upload" | "importing" | "success" | "error") {
        switch (stage) {
            case "segment-select":
                this.progress.set(30);
                this.stage.set("segment-select");
                break;
            case "upload":
                this.progress.set(65);
                this.stage.set("upload");
                if (this.importMethod() === "csv") {
                    this.setupDropzone();
                }
                break;
            case "importing":
                this.progress.set(85);
                this.stage.set("importing");
                let file: File | null = null;
                if (this.importMethod() === "paste") {
                    //build a csv from the pasted emails
                    const csvContent = ["email", ...this.pastedSubscribers()].join("\n");
                    file = new File([csvContent], "imported_emails.csv", {
                        type: "text/csv",
                        lastModified: new Date().getTime()
                    });
                } else {
                    file = this.csvFile();
                }
                if (file) {
                    let segment = PredefinedSegment.All;
                    if (this.useSegments()) {
                        const segmentName = this.segmentSelect();
                        if (segmentName === "actualReaders") {
                            segment = PredefinedSegment.ActualReaders;
                        } else if (segmentName === "freeSubscribers") {
                            segment = PredefinedSegment.FreeSubscribers;
                        } else if (segmentName === "arcReaders") {
                            segment = PredefinedSegment.ArcReaders;
                        }
                    }
                    this._importManagerService.upload(file, {segments: [segment], forceWelcomeAutomation: this.forceWelcomeAutomation()});
                }
                break;
            case "success":
                this.progress.set(100);
                this.stage.set("success");
                break;
            case "error":
                this.progress.set(100);
                this.stage.set("error");
                break;
            default:
                break;
        }
    }

    setImportMethod(method: "csv" | "paste") {
        const current = this.importMethod();
        this.importMethod.set(method);
        if (method === "csv" && current !== "csv") {
            this.setupDropzone();
        }
    }

    async setupDropzone() {
        if (this.csvFile()) {
            return;
        }
        await new Promise(resolve => setTimeout(resolve, 300));
        const dropzone = new Dropzone(document.querySelector<HTMLElement>("#csv-import-dropzone")!, {
            url: "https://url.com",
            clickable: [".dropzone", ".btn-upload", ".thingy"],
            acceptedFiles: "text/csv",
            maxFiles: 1,
            autoQueue: false,
            autoProcessQueue: false,
            //@ts-ignore
            disablePreviews: true,
        });
        document.querySelector<HTMLDivElement>(".dz-message")!.style.display = "none";
        dropzone.on("addedfile", async (file) => {
            if (file.type !== "text/csv") {
                dropzone.removeFile(file);
                return;
            }
            this.csvFile.set(file);
            this.analyzingCSV.set(true);
            try {
                const [_, count] = await Promise.all([
                    new Promise(resolve => setTimeout(resolve, 2000)),
                    this._processCSV(file)
                ]);
                if (count) {
                    this.csvSubscriberCount.set(count);
                    dropzone.destroy();
                    this.csvWarning.set(false);
                } else {
                    this.csvFile.set(null);
                    this.csvWarning.set(true);
                }
            } catch (_) {
                this.csvFile.set(null);
                this.csvWarning.set(true);
            } finally {
                this.analyzingCSV.set(false);
            }
        });
    }

    resetUploader() {
        this.csvFile.set(null);
        this.analyzingCSV.set(false);
        this.csvSubscriberCount.set(0);
        this.csvWarning.set(false);
        this.setupDropzone();
    }

    freeMigration() {
        this._featureBaseService.open("I want a free subscriber list migration!");
    }

    support() {
        this._featureBaseService.open("I need help with my subscriber list import.");
    }

    private async _processCSV(file: File) {
        const stream = file.stream().pipeThrough(new TextDecoderStream()).getReader();
        const uniqueEmails = new Set();

        // Configuration
        const targetHeaders = ["email", "email address"];
        // Regex: Allow non-whitespace @ non-whitespace . non-whitespace
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        // State Machine
        let inQuotes = false;
        let currentBuffer = "";
        let colIndex = 0;
        let rowIndex = 0;

        // Logic State
        let emailColIndex = -1; // -1 means "header not found"
        let rowCandidates: { text: string, colIdx: number }[] = [];

        while (true) {
            const { done, value } = await stream.read();

            // 1. Process Chunk
            if (value) {
                for (let i = 0; i < value.length; i++) {
                    const char = value[i];

                    // Handle Quotes
                    if (char === '"') {
                        inQuotes = !inQuotes;
                        continue;
                    }

                    // Handle Delimiters (Comma or Newline)
                    if (!inQuotes && (char === ',' || char === '\n')) {
                        finalizeCell(char);
                        continue;
                    }

                    // Accumulate Character
                    currentBuffer += char;
                }
            }

            // 2. Process End of Stream
            if (done) {
                // If file doesn't end with \n, process the last buffer remaining
                if (currentBuffer.length > 0) {
                    finalizeCell('\n');
                }
                break;
            }
        }

        // --- Helper Functions (defined inside to access state) ---

        function finalizeCell(delimiter: string) {
            // Clean cell value
            const cellText = currentBuffer.trim();
            currentBuffer = ""; // Reset buffer immediately

            if (rowIndex === 0) {
                // --- HEADER ROW LOGIC ---
                // We are just building the index, not collecting emails yet (usually)
                // Note: If you want to count emails in the header row itself, remove this 'else'.
                // Usually, we just check for the column name here.
                const cleanHeader = cellText.toLowerCase().replace(/^"|"$/g, '');
                if (targetHeaders.includes(cleanHeader)) {
                    emailColIndex = colIndex;
                }
            } else {
                // --- DATA ROW LOGIC ---
                // 1. Clean quotes from CSV format ("value" -> value)
                let cleanText = cellText;
                if (cleanText.startsWith('"') && cleanText.endsWith('"')) {
                    cleanText = cleanText.slice(1, -1).trim();
                }

                // 2. Check if valid email
                if (emailRegex.test(cleanText)) {
                    rowCandidates.push({ text: cleanText.toLowerCase(), colIdx: colIndex });
                }
            }

            // Handle Row End
            if (delimiter === '\n') {
                finalizeRow();
            } else {
                colIndex++;
            }
        }

        function finalizeRow() {
            // If it's the header row, we just finish up and move on
            if (rowIndex === 0) {
                rowIndex++;
                colIndex = 0;
                return;
            }

            let priorityMatchFound = false;

            // RULE 1: If we have a known email column, try to find a match there first
            if (emailColIndex > -1) {
                const match = rowCandidates.find(c => c.colIdx === emailColIndex);
                if (match) {
                    uniqueEmails.add(match.text);
                    priorityMatchFound = true;
                }
            }

            // RULE 2: Fallback (No header OR specific column was empty/invalid)
            // Add ALL valid emails found in this row
            if (!priorityMatchFound && rowCandidates.length > 0) {
                for (const candidate of rowCandidates) {
                    uniqueEmails.add(candidate.text);
                }
            }

            // Reset for next row
            rowIndex++;
            colIndex = 0;
            rowCandidates = [];
        }

        return uniqueEmails.size;
    }
}
